import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  edgeUsersApi,
  useGetUserSettingsQuery,
  useSaveUserSettingsMutation,
  type SaveUserSettingsResponse,
} from '@/services/api/users-api'
import { useAppDispatch } from '@/store/hooks'
import { getSettingsCache, setSettingsCache } from '@/workspace/idb-cache'
import { mirrorOnce, noteMirrored } from '@/workspace/mirror-once'
import { SerialQueue } from '@/workspace/serial-queue'

// Module-scope per-(page, type) FIFO queues. Multiple hook instances for the
// same setting type share one queue so concurrent saves are serialized at the
// API layer and every payload is sent in order. Different types are
// independent.
const queues = new Map<string, SerialQueue>()

function getQueue(page: string, type: string): SerialQueue {
  const key = `${page}:${type}`
  let q = queues.get(key)
  if (!q) {
    q = new SerialQueue()
    queues.set(key, q)
  }
  return q
}

/** Test-only: drop all queues so each test starts in a known state. */
export function __resetUserSettingsQueuesForTest(): void {
  queues.clear()
}

/** Setting page identifiers — match the backend SK first segment. */
export const SETTING_PAGES = {
  GLOBAL: 'global',
  EXPRESSIONS: 'expressions',
} as const

/** Setting type identifiers — match the backend SK second segment. */
export const SETTING_TYPES = {
  COLUMN_FAVORITES: 'columnFavorites',
  /** Expression library — paired with SETTING_PAGES.EXPRESSIONS. One per namespace. */
  EXPRESSION_REALTIME: 'realtime',
  EXPRESSION_HISTORICAL: 'historical',
} as const

interface UseUserSettingsOptions {
  /** Setting page key (e.g. 'global', 'expressions'). */
  page: string
  /** Setting type key (e.g. 'columnFavorites', 'realtime'). */
  type: string
  /** Whether to auto-load on mount. */
  autoLoad?: boolean
}

interface UseUserSettingsResult<T> {
  setting: T[]
  isLoading: boolean
  /**
   * Persist an updated setting. Returns the API response envelope, or `null`
   * if the API call failed (the local IDB and RTK caches are still updated
   * optimistically).
   */
  save: (setting: T[]) => Promise<SaveUserSettingsResponse | null>
  reload: () => void
}

/**
 * Loads and saves a user setting (singleton blob or expression library)
 * via the user API. Two-layer cache pattern:
 *
 *   - RTK Query — the runtime source of truth once the request returns.
 *   - IndexedDB cold-start cache — populated by previous successful
 *     fetches (and by `save()`). Used to render before the network
 *     responds, and as the fallback if the API is unreachable.
 *
 * `isLoading` stays `true` until the IDB read resolves AND the RTK
 * Query request resolves with non-empty data. Consumers can render
 * loading state during this window without flicker.
 *
 * On `save()`:
 *   - The IDB cache is updated immediately (don't block on it — IDB
 *     writes are background-cheap).
 *   - The RTK Query cache is optimistically patched so subscribed
 *     components see the new value on the next render.
 *   - The API write happens in the background, serialised through a
 *     per-(page, type) ``SerialQueue``. Failure resolves to ``null``
 *     so the caller can detect it without halting the queue.
 */
export function useUserSettings<T = unknown>({
  page,
  type,
  autoLoad = true,
}: UseUserSettingsOptions): UseUserSettingsResult<T> {
  const {
    data: rawData,
    isLoading: rtkLoading,
    refetch,
  } = useGetUserSettingsQuery({ page, type }, { skip: !autoLoad })

  const dispatch = useAppDispatch()
  const [saveMutation] = useSaveUserSettingsMutation()

  // ─── IDB cold-start cache ──────────────────────────────────────────
  // `idbCache` holds whatever IDB returned for this (page, type). It's
  // null until the IDB read has resolved. After resolution, it's either
  // an array (possibly empty) or stays null on read error.
  const [idbCache, setIdbCache] = useState<T[] | null>(null)
  const [idbHydrated, setIdbHydrated] = useState(false)

  useEffect(() => {
    let cancelled = false
    setIdbHydrated(false)
    setIdbCache(null)
    getSettingsCache<T>(page, type)
      .then((cached) => {
        if (cancelled) return
        setIdbCache(cached)
        setIdbHydrated(true)
      })
      .catch(() => {
        if (cancelled) return
        setIdbHydrated(true) // hydrated with null = treat as cold start
      })
    return () => {
      cancelled = true
    }
  }, [page, type])

  useEffect(() => {
    if (Array.isArray(rawData) && rawData.length > 0) {
      mirrorOnce(`settings:${page}:${type}`, rawData, () => {
        void setSettingsCache(page, type, rawData)
      })
      setIdbCache(rawData as T[])
    }
  }, [rawData, page, type])

  const setting: T[] = useMemo(() => {
    if (Array.isArray(rawData) && rawData.length > 0) return rawData as T[]
    return idbCache ?? []
  }, [rawData, idbCache])

  const save = useCallback(
    (updated: T[]): Promise<SaveUserSettingsResponse | null> => {
      noteMirrored(`settings:${page}:${type}`, updated)
      void setSettingsCache(page, type, updated)
      setIdbCache(updated)
      dispatch(edgeUsersApi.util.updateQueryData('getUserSettings', { page, type }, () => updated))

      return new Promise<SaveUserSettingsResponse | null>((resolve) => {
        getQueue(page, type).push<SaveUserSettingsResponse | null>(
          async () => {
            try {
              return (await saveMutation({
                page,
                type,
                setting: updated,
              }).unwrap()) as SaveUserSettingsResponse
            } catch {
              return null
            }
          },
          {
            onSuccess: (r) => resolve(r),
            onError: () => resolve(null),
          },
        )
      })
    },
    [saveMutation, dispatch, page, type],
  )

  const reload = useCallback(() => {
    refetch()
  }, [refetch])

  const isLoading = useMemo(() => {
    if (!idbHydrated) return true
    return rtkLoading
  }, [idbHydrated, rtkLoading])

  return { setting, isLoading, save, reload }
}
